import { Inject, Injectable } from '@angular/core';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

export const USER_INFO_TOKEN = 'userInfo';
export const REQUEST_SENT = 'requestSent';
export const USER_SETTINGS_TOKEN = 'userSettings';
export const LOCALE_ID = 'localeId';
export const ACTIVE_ENTITIES = 'activeEntities';
export const TOTAL_ENTITIES = 'totalEntities';
export const USER_SETTINGS = 'USER_SETTINGS';
export const REPORT_LOCALE_CHANGED = 'REPORT_LOCALE_CHANGED';
export const USER_LOCATION_PERMS = 'userLocationPerms';
export const USER_RECORD_PERM = 'userRecordPerm';

export const enum StorageType {
  local = 'local',
  session = 'session',
}

interface EntitySubTab {
  name: string;
  isChecked: boolean;
  recordId: string;
}

interface EntityObj {
  name: string;
  isChecked: boolean;
  recordId: string;
}

interface TotalEntityItem {
  entityObj: EntityObj;
  subTabList: EntitySubTab[];
  position: number;
}

@Injectable({ providedIn: 'root' })
export class AppStorageService {
  constructor(@Inject(CbreLoggerToken) private readonly logger: CbreLogger) {}

  public getItem<T>(token: string, storageType: StorageType = StorageType.local): T {
    const value = this.getStorage(storageType).getItem(token);
    try {
      return JSON.parse(value) as T;
    } catch {
      this.logger.warn(`token ${token} not able to be returned as JSON, returning as is`);
      return value as unknown as T;
    }
  }

  public getItemOrDefault<T>(token: string, defaultValue: T, storageType: StorageType = StorageType.local): T {
    const value = this.getItem<T>(token, storageType);
    return value !== null ? value : defaultValue;
  }

  public setItem<T>(token: string, item: T, storageType: StorageType = StorageType.local) {
    this.getStorage(storageType).setItem(token, JSON.stringify(item));
  }

  public removeItem(token: string, storageType: StorageType = StorageType.local) {
    this.getStorage(storageType).removeItem(token);
  }

  public clear(storageType: StorageType = StorageType.local) {
    this.getStorage(storageType).clear();
  }

  public clearExcept(keysToPreserve: string[], storageType: StorageType = StorageType.local) {
    const storage = this.getStorage(storageType);
    // get the list of items to remove
    const keysToRemove = Object.keys(storage).filter(key => !keysToPreserve.includes(key));
    // remove each key to remove
    keysToRemove.forEach(k => storage.removeItem(k));
  }

  private getStorage(storageType: StorageType): Storage {
    switch (storageType) {
      case StorageType.local:
        return window.localStorage;
      case StorageType.session:
        return window.sessionStorage;
      default:
        return window.localStorage;
    }
  }

  public remindTheirChoice(dialogId: string, token: string): boolean | null {
    const confirmDialogValues = this.getItem(token) || {};
    const value = confirmDialogValues[dialogId];
    return value != null ? value : null;
  }

  /**
   * Checks if a specific tab name exists in the totalEntities localStorage data and is checked
   * @param tabName - The name of the tab to search for (e.g., "Alias", "Details", etc.)
   * @returns boolean - true if the tab exists and isChecked is true, false otherwise
   */
  public isTabExistsInTotalEntities(tabName: string): boolean {
    const entities = this.getItem<TotalEntityItem[]>(TOTAL_ENTITIES) || [];
    return entities.some(entity =>
      entity.subTabList?.some(tab => {
        const nameMatches = tab.name?.toLowerCase() === tabName.toLowerCase();
        return nameMatches && tab.isChecked === true;
      })
    );
  }
}
